import { Square as GameSquare } from "../services/zodiacEngine/Square";
import React, { useContext, ReactNode, useState, useEffect } from "react";
import styles from "./Square.module.css";
import globalStyles from "../App.module.css";
import {
  IMove,
  IPiece,
  ISideEffect,
  ISquare,
  Mode,
  MoveType,
  Position,
  EffectType,
  PieceType,
  State,
} from "../services/zodiacEngine/types";
import SquareRenderer from "./SquareRenderer"; // Import the renderer
import ZodiacEngineService from "./ZodiacEngineService";
import { GameContext } from "./Providers/GameContext";
import { SideEffect } from "../services/zodiacEngine/SideEffect";
import {
  getTutorialTerrain,
  getTerrainImagePath,
  shouldTerrainRenderAsOverlay,
} from "./Tutorials/Terrain";

// New interface to track movement destination highlight
interface MovementDestination {
  row: number;
  col: number;
}

interface SquareProps {
  square: GameSquare;
  position: Position;
  moves: IMove[];
  sideEffects: ISideEffect[];
  //highlightedSquares?: Position[];
  //onHover: (move?: IMove) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>, position: Position) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onSquareClick?: (position: Position) => void; // For mobile click-to-place
  // Callback for when a piece on the board starts being dragged (Sandbox/PuzzleDesigner)
  onPieceDragStart?: (event: React.DragEvent<HTMLImageElement>, piece: IPiece, position: Position) => void;
  // When true, suppresses piece selection and shows crosshair cursor (PuzzleDesigner designation mode)
  designateMode?: boolean;
  // When set, restricts piece selection/movement to only the specified side (used by Puzzles bestMove)
  puzzlePlayerIsLight?: boolean;
  // Add props to handle hover highlight
  hoveredDestination?: MovementDestination | null;
  setHoveredDestination?: (destination: MovementDestination | null) => void;
  // Flag to indicate this square is part of a home row highlight
  isHomeRowHighlight?: boolean;
  // Tutorial information for special highlighting
  tutorialInfo?: {
    constellation: string;
    tutorialIndex: number;
    hasFirstClickHappened?: boolean;
    activeLossEffect?: {
      type: string;
      position: { row: number; col: number };
    } | null;
    suppressTutorialHighlights?: boolean;
  };
}

function Square(squareProps: SquareProps) {
  // Add state to track movement destination highlight for this square
  const [isDestinationHighlighted, setIsDestinationHighlighted] =
    useState(false);

  const {
    potentialMoves,
    gameState,
    selectedPiece,
    isThisPlayersTurn,
    updatePotentialMoves,
    updateSelectedPiece,
    isLightPlayer,
    selectionLocked,
    setSelectionLocked,
    isObserveMode,
    // King Guess taunt feature
    setKingGuess,
    clearKingGuess,
    getKingGuessForPiece,
  } = useContext(GameContext)!;

  // Function to check if current square should be highlighted as movement destination
  const checkHighlightDestination = () => {
    if (!squareProps.hoveredDestination) return false;
    return (
      squareProps.position.row === squareProps.hoveredDestination.row &&
      squareProps.position.col === squareProps.hoveredDestination.col
    );
  };

  // Update local highlight state when the props change
  React.useEffect(() => {
    setIsDestinationHighlighted(checkHighlightDestination());
  }, [squareProps.hoveredDestination]);

  // Check if this square should show the tutorial goal row highlight
  const shouldShowGoalRowHighlight = () => {
    if (!squareProps.tutorialInfo) return false;

    // Don't show tutorial highlights when loss effects are active
    if (squareProps.tutorialInfo.suppressTutorialHighlights) return false;

    const { constellation, tutorialIndex } = squareProps.tutorialInfo;

    // Bear tutorial 2 (index 1) - highlight row 7 as the goal
    if (constellation === "Bear" && tutorialIndex === 1) {
      return squareProps.position.row === 7;
    }

    return false;
  };

  // Check if this square should show the tutorial piece glow
  const shouldShowTutorialPieceGlow = () => {
    if (!squareProps.tutorialInfo) return false;

    // Don't show tutorial highlights when loss effects are active
    if (squareProps.tutorialInfo.suppressTutorialHighlights) return false;

    const { constellation, tutorialIndex, hasFirstClickHappened } =
      squareProps.tutorialInfo;

    // Only show glow before first click has happened
    if (hasFirstClickHappened) return false;

    // Bear tutorial 1 (index 0) - highlight the bear piece to guide first click
    if (constellation === "Bear" && tutorialIndex === 0) {
      // Check if this square has a light Bear piece
      return squareProps.square.Pieces.some(
        (piece) => piece.type === "Bear" && piece.isLight
      );
    }

    return false;
  };

  // Check if this square should show the tutorial danger row highlight (red)
  const shouldShowTutorialDangerRow = () => {
    if (!squareProps.tutorialInfo) return false;

    // Don't show tutorial highlights when loss effects are active
    if (squareProps.tutorialInfo.suppressTutorialHighlights) return false;

    const { constellation, tutorialIndex } = squareProps.tutorialInfo;

    // Bear tutorial 4 (index 3) "Race defence / Kingslayer" - highlight bottom row (row 0) to show where to block enemy
    if (constellation === "Bear" && tutorialIndex === 3) {
      return squareProps.position.row === 0;
    }

    return false;
  };

  // Check if this square should show the tutorial enemy target highlight (golden glow around enemy)
  const shouldShowTutorialEnemyTarget = () => {
    if (!squareProps.tutorialInfo) return false;

    // Don't show tutorial highlights when loss effects are active
    if (squareProps.tutorialInfo.suppressTutorialHighlights) return false;

    const { constellation, tutorialIndex } = squareProps.tutorialInfo;

    // Bear tutorial 4 (index 3) "Race defence / Kingslayer" - highlight the dark crane to show capture target
    if (constellation === "Bear" && tutorialIndex === 3) {
      // Check if this square has a dark Crane piece
      return squareProps.square.Pieces.some(
        (piece) => piece.type === "Crane" && !piece.isLight
      );
    }

    return false;
  };

  // Function to check if a CSS class exists
  const checkStyleExists = (className: string): boolean => {
    return !!styles[className];
  };

  const handleEntangleHover = (
    sideEffect: ISideEffect,
    isHovering: boolean
  ) => {
    if (!squareProps.setHoveredDestination) return;

    if (isHovering && sideEffect.effect.type === EffectType.entangle) {
      // Check if this is a side effect with a moveToPosition property (move entangle)
      if ((sideEffect as any).moveToPosition) {
        // Check if this is a triple entangle (adjacent enemy)
        if ((sideEffect as any).tripleEntangle) {
          // For triple entangle, directly use the moveToPosition
          squareProps.setHoveredDestination({
            row: (sideEffect as any).moveToPosition.row,
            col: (sideEffect as any).moveToPosition.col,
          });
          return;
        }

        // Show the destination where the octopus will move when this entangle is clicked
        // Engine directions now match visual expectations, so use moveToPosition directly
        squareProps.setHoveredDestination({
          row: (sideEffect as any).moveToPosition.row,
          col: (sideEffect as any).moveToPosition.col,
        });
      } else {
        // For regular entangles without moveToPosition (e.g., knight move or double diagonal)
        // Find the move that this side effect belongs to
        const move = potentialMoves.find((m) =>
          m.sideEffects.some((se) => se.id === sideEffect.id)
        );

        if (move) {
          // Highlight the destination square where the octopus will move
          squareProps.setHoveredDestination({
            row: move.position.row,
            col: move.position.col,
          });
        }
      }
    } else if (!isHovering) {
      // Clear hover destination
      squareProps.setHoveredDestination(null);
    }
  };

  const handlePieceClick = (piece?: IPiece) => {

    // ==========================================
    // SAFETY VALVE (Layer 2): Recover from dead state
    // If selectionLocked is true but selectedPiece is undefined,
    // the player is stuck. Re-select the bonus turn piece to recover.
    // ==========================================
    if (selectionLocked && !selectedPiece && gameState.activeBonusTurnPieceId) {
      const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
      for (let r = 0; r < currentGameState.board.length; r++) {
        for (let c = 0; c < currentGameState.board[r].length; c++) {
          const bonusPiece = currentGameState.board[r][c].Pieces.find(
            (p) => p.id === currentGameState.activeBonusTurnPieceId
          );
          if (bonusPiece) {
            const bonusPosition = { row: r, col: c };
            updateSelectedPiece({ piece: bonusPiece, position: bonusPosition });
            const validMoves = ZodiacEngineService.zodiacEngine.getValidMoves(bonusPosition, bonusPiece);
            updatePotentialMoves(validMoves);
            return; // Recovery done - let the next click proceed normally
          }
        }
      }
      // If piece not found at all, unlock as last resort to prevent permanent stuck state
      setSelectionLocked(false);
      return;
    }

    // ==========================================
    // OCCUPIED-SQUARE CAPTURE FIX (global)
    // ==========================================
    // Multi-piece squares (e.g. an enemy Snake housed in an enemy Tree) render
    // through `.floatingIcons` at z-index 10, which paints ABOVE the move
    // indicator (`.moveIndicator`, z-index 3). A click therefore lands on the
    // piece <img> (this handler) instead of the capture indicator, so the
    // capture never fires and the enemy piece gets selected instead.
    //
    // If the currently-selected piece has a clickable move onto THIS square,
    // execute it here. This is identical to clicking the (obscured) indicator,
    // so any attacker (Crane, Bear, Frog, ...) can capture an enemy housed in a
    // tree by clicking it. The move still passes through movePiece()'s normal
    // legality / bonus-turn / selection-lock checks.
    if (selectedPiece && !isObserveMode) {
      const moveOntoSquare =
        squareProps.moves.find(
          (m) => m.moveType === MoveType.capture && isMoveClickable(m)
        ) || squareProps.moves.find((m) => isMoveClickable(m));
      if (moveOntoSquare) {
        handleMoveClick(moveOntoSquare);
        return;
      }
    }

    // Puzzle side restriction: only allow selecting pieces matching the puzzle player's side
    // (but allow zombified enemy pieces — they've switched teams)
    if (squareProps.puzzlePlayerIsLight !== undefined && piece) {
      const isZombifiedEnemy = piece.zombifiedStatus?.isZombified && piece.isLight !== squareProps.puzzlePlayerIsLight;
      if (piece.isLight !== squareProps.puzzlePlayerIsLight && !isZombifiedEnemy) {
        return;
      }
    }

    // ==========================================
    // BONUS TURN GUARD: Use engine truth (activeBonusTurnPieceId)
    // to close the 300ms window where selectionLocked is not yet set
    // ==========================================
    const activeBonusId = gameState.activeBonusTurnPieceId;
    if (activeBonusId) {
      // Block selecting any non-bonus piece
      if (!piece || piece.id !== activeBonusId) {
        return;
      }
      // Bonus piece clicked but not yet selected (300ms window) — re-select + handle endTurn
      if (!selectedPiece) {
        updateSelectedPiece({ piece, position: squareProps.position });
        const validMoves = ZodiacEngineService.zodiacEngine.getValidMoves(squareProps.position, piece);
        updatePotentialMoves(validMoves);
        setSelectionLocked(true);
        const endTurnMove = validMoves.find(m => m.moveType === MoveType.endTurn);
        if (endTurnMove) { movePiece(endTurnMove); }
        return;
      }
      // Already selected — fall through to existing autoExecute/endTurn logic
    }

    // ==========================================
    // Phase 4 - SOC Refactoring: Use SelectionManager
    // ==========================================
    // NOTE: Zombie ink trap moved to movePiece() - selection is now allowed,
    // trap only triggers when owner actually tries to MOVE a shadow
    // All selection validation logic moved to engine layer

    const selectionManager = ZodiacEngineService.zodiacEngine.selectionManager;

    // Validate if this selection is allowed
    const validation = selectionManager.canSelectPiece(piece, squareProps.position, {
      isLightPlayer: isLightPlayer(),
      isThisPlayersTurn: isThisPlayersTurn(),
      selectionLocked,
      currentSelectedPiece: selectedPiece
    });

    // Log validation result

    // Block if not allowed
    if (!validation.allowed) {
      return;
    }

    // Penguin debug logging
    if (piece) {
      selectionManager.logPenguinSelection(piece, gameState);
    }

    // Handle virus auto-execution
    if (validation.autoExecute && piece && selectedPiece) {
      const validMoves = ZodiacEngineService.zodiacEngine.getValidMoves(
        squareProps.position,
        piece
      );
      const endTurnMove = selectionManager.findVirusEndTurnMove(validMoves);

      if (endTurnMove) {
        movePiece(endTurnMove);
      }
      return;
    }

    // If clicking same piece during lock, just return (already validated)
    if (selectionLocked && piece && selectedPiece && piece.id === selectedPiece.piece.id) {
      return;
    }

    // In observe mode, clicking the same piece again should deselect it
    if (isObserveMode && piece && selectedPiece && piece.id === selectedPiece.piece.id) {
      deselectPiece();
      return;
    }

    // Normal behavior
    if (piece) {
      squareClickHandler(squareProps.square, squareProps.position, piece);
    } else {
      // deselect on clicking an empty square
      deselectPiece();
    }
  };

  function squareClickHandler(
    square: ISquare,
    position: Position,
    pieceClicked?: IPiece,
    sideEffect?: ISideEffect
  ) {

    if (sideEffect) {
      const move: IMove = potentialMoves.find(
        (move) => move.id === sideEffect.moveId
      )!;
      movePiece(move);
    }

    // If selection is locked, prevent deselecting or changing selection
    if (selectionLocked) {
      return;
    }

    if (square.Pieces.length === 0) {
      deselectPiece();
      return;
    }

    if (!pieceClicked) {
      return;
    }

    getValidMoves(position, pieceClicked);
  }

  function getValidMoves(position: Position, pieceClicked: IPiece) {

    updateSelectedPiece({ piece: pieceClicked, position: position });

    const selectionManager = ZodiacEngineService.zodiacEngine.selectionManager;
    const validMoves = ZodiacEngineService.zodiacEngine.getValidMoves(position, pieceClicked);

    // Check for virus auto-execution (Phase 4 - SOC)
    if (selectionManager.shouldAutoExecuteVirus(pieceClicked, validMoves, selectionLocked)) {
      setTimeout(() => {
        movePiece(validMoves[0]);
      }, 100);
    }

    updatePotentialMoves(validMoves);

    // Delayed check for virus auto-execution when selection gets locked
    if (pieceClicked.type === PieceType.Virus) {
      setTimeout(() => {
        if (
          selectionLocked &&
          selectedPiece &&
          selectedPiece.piece.id === pieceClicked.id
        ) {
          const currentValidMoves =
            ZodiacEngineService.zodiacEngine.getValidMoves(position, pieceClicked);

          if (selectionManager.shouldAutoExecuteVirus(pieceClicked, currentValidMoves, selectionLocked)) {
            movePiece(currentValidMoves[0]);
          }
        }
      }, 300);
    }
  }

  function deselectPiece() {
    // Phase 4 - SOC: Use SelectionManager for deselection validation
    const selectionManager = ZodiacEngineService.zodiacEngine.selectionManager;

    if (!selectionManager.canDeselectPiece(selectionLocked) || gameState.activeBonusTurnPieceId) {
      return;
    }

    updatePotentialMoves([]);
    updateSelectedPiece(undefined);
  }

  async function movePiece(move: IMove) {
    // Clear hover destination before making the move
    if (squareProps.setHoveredDestination) {
      squareProps.setHoveredDestination(null);
    }

    // Early return if no selected piece
    if (!selectedPiece) {
      return;
    }

    // Early return if move is not valid
    if (!move) {
      return;
    }

    // BONUS TURN MOVE GUARD: Prevent non-bonus pieces from executing moves
    const activeBonusId = gameState.activeBonusTurnPieceId;
    if (activeBonusId && selectedPiece.piece.id !== activeBonusId) {
      return;
    }

    // ==========================================
    // ZOMBIE INK TRAP: Check if trying to move a shadow octopus
    // ==========================================
    // The owner clicked a shadow (allowed for selection) but now tries to MOVE it
    // This triggers the trap - the shadow disappears and turn ends
    if (selectedPiece.piece.isShadow && selectedPiece.piece.isZombieInkShadow) {
      const trapped = ZodiacEngineService.zodiacEngine.attemptMoveZombieInkShadow(
        selectedPiece.piece,
        selectedPiece.position
      );
      if (trapped) {
        // Trap triggered - clear selection and update UI
        updatePotentialMoves([]);
        updateSelectedPiece(undefined);
        return;
      }
    }

    // Phase 4 - SOC: Use SelectionManager for move validation during lock
    const selectionManager = ZodiacEngineService.zodiacEngine.selectionManager;

    // Log locked move attempts for debugging
    if (selectionLocked) {
    }

    // Validate move during selection lock
    if (selectionLocked) {
      // CRITICAL: Get FRESH game state from engine for Firebase sync compatibility
      const freshGameState = ZodiacEngineService.zodiacEngine.getGameState();
      const moveValidation = selectionManager.canExecuteMoveDuringLock(
        move,
        selectionLocked,
        freshGameState
      );

      if (!moveValidation.allowed) {
        return;
      }
    }

    // Attempt the move
    const moveSuccessful = await ZodiacEngineService.zodiacEngine.makeMove(
      selectedPiece.position,
      move.position,
      selectedPiece.piece,
      move.toPiece,
      move.moveType,
      move.sideEffects
    );

    if (moveSuccessful) {
      // Clear potential moves after successful move
      // BonusTurnHandler will handle auto-reselection for bonus turn pieces
      updatePotentialMoves([]);

      // CRITICAL FIX: Clear selectedPiece after move to prevent selection indicator
      // from staying at the old position (especially important in Sandbox mode)
      updateSelectedPiece(undefined);
    }
  }

  const findPieceById = (id: string): IPiece => {
    const piece = gameState.board
      .flatMap((row) => row.flatMap((square) => square.Pieces))
      .find((piece) => piece.id === id);

    if (!piece) {
      throw new Error(`Piece with ID ${id} not found on the board`);
    }

    return piece;
  };

  function isSideEffectClickable(sideEffect: ISideEffect): boolean {
    try {
      const move = findMoveFromSideEffect(sideEffect);
      return move ? isMoveClickable(move) : false;
    } catch (error) {
      // Move not found - this can happen during animations when game state updates
      return false;
    }
  }

  function isMoveClickable(move: IMove): boolean {
    // Disable all move interactions in Rules mode
    if (gameState.mode === Mode.Rules) {
      return false;
    }

    // Find piece in the game state that the move is referring to. Stale move
    // data can outlive the piece during the render that immediately follows
    // a capture (e.g. AI Monkey leaping onto the user's Crane King in
    // Crane 1 — the piece is gone but a leftover potential-move still
    // references its id). Don't crash the whole UI for that; just treat the
    // move as unclickable.
    let pieceToMove: IPiece;
    try {
      pieceToMove = findPieceById(move.pieceId);
    } catch {
      return false;
    }

    // Allow if it's your own piece
    if (pieceToMove.isLight === isLightPlayer() && isThisPlayersTurn()) {
      return true;
    }

    // Allow if it's an enemy piece that is zombified
    if (pieceToMove.zombifiedStatus.isZombified && isThisPlayersTurn()) {
      return true;
    }

    // Check for Local 2P mode - enforce turn order
    const challengeConfig = (gameState as any).challengeAIConfig;
    if (challengeConfig) {
      const { lightPlayer, darkPlayer } = challengeConfig;
      // In Local 2P mode, only allow moves for pieces matching the current turn
      if (lightPlayer === 'player' && darkPlayer === 'player') {
        // Check if piece color matches current turn (accounting for zombification)
        const effectivelyLight = pieceToMove.zombifiedStatus.isZombified
          ? !pieceToMove.isLight
          : pieceToMove.isLight;
        return effectivelyLight === gameState.isLightTurn;
      }
    }

    // Allow all moves in Dev, Sandbox, TestAI, and Tutorial modes (local control of both sides)
    if (gameState.mode === Mode.Dev || gameState.mode === Mode.Sandbox || gameState.mode === Mode.TestAI || gameState.mode === Mode.Tutorial) {
      // In puzzle mode, only allow moves for the player's side
      if (gameState.mode === Mode.Sandbox && squareProps.puzzlePlayerIsLight !== undefined) {
        return pieceToMove.isLight === squareProps.puzzlePlayerIsLight || pieceToMove.zombifiedStatus?.isZombified;
      }
      return true;
    }

    return false;
  }

  const getStyle = (): string => {
    let style = globalStyles.square;

    // Add golden highlight for home row during piece placement
    if (squareProps.isHomeRowHighlight) {
      style += ` ${styles.homeRowHighlight}`;
    }

    return style;
  };

  const handleMoveClick = (move: IMove) => {
    if (selectedPiece) {
      // Create a copy of the move without any entangle side effects
      const moveWithoutEntangle = {
        ...move,
        sideEffects: move.sideEffects.filter(
          (effect) => effect.effect.type !== EffectType.entangle
        ),
      };
      // Execute the move without entanglement
      movePiece(moveWithoutEntangle);
    }
  };

  const findMoveFromSideEffect = (sideEffect: SideEffect): IMove | null => {
    var move = potentialMoves.find((move) => move.id === sideEffect.moveId);
    if (!move) {
      return null;
    }
    return move;
  };

  // Utility: Map {dRow, dCol} to direction string
  function vectorToDirection(dir: { dRow: number; dCol: number }): string {
    if (dir.dRow === 1 && dir.dCol === -1) return "up-left";
    if (dir.dRow === 1 && dir.dCol === 0) return "up";
    if (dir.dRow === 1 && dir.dCol === 1) return "up-right";
    if (dir.dRow === 0 && dir.dCol === -1) return "left";
    if (dir.dRow === 0 && dir.dCol === 1) return "right";
    if (dir.dRow === -1 && dir.dCol === -1) return "down-left";
    if (dir.dRow === -1 && dir.dCol === 0) return "down";
    if (dir.dRow === -1 && dir.dCol === 1) return "down-right";
    return "down"; // default
  }

  const handleSideEffectClick = (
    sideEffect: ISideEffect,
    clickedDirection?: string
  ) => {
    if (sideEffect.effect.type === EffectType.rotate) {
      if (!selectedPiece) return;

      // Find the rotation move
      const move = findMoveFromSideEffect(sideEffect);
      if (!move) {
        return;
      }

      // Check if this rotation grants a bonus turn (zombified penguin)
      const hasBonusTurn = move.sideEffects.some(
        (se) => se.effect.type === EffectType.bonusTurn
      );

      if (hasBonusTurn) {
        // ZOMBIFIED PENGUIN: Execute rotation as a move (grants bonus turn)
        movePiece(move);
        return;
      } else {
        // REGULAR PENGUIN: Manual rotation (free action, not a move)
        const penguin = findPieceById(selectedPiece.piece.id);
        if (!penguin) return;

        const dir = (sideEffect as any).rotateDirection;
        if (!dir) return;

        // Set the penguin's direction directly
        penguin.direction = dir;


        // Trigger a no-move to update the game state
        ZodiacEngineService.zodiacEngine.makeMove(
          selectedPiece.position,
          selectedPiece.position,
          penguin,
          null,
          MoveType.noMove,
          [sideEffect]
        );

        // Clear selection and potential moves
        updatePotentialMoves([]);
        updateSelectedPiece(undefined);
        return;
      }
    }
    if (!selectedPiece) return;
    var move = findMoveFromSideEffect(sideEffect);
    if (!move) {
      return;
    }

    // Handle the special case for split entangle
    // Check if this is an entangle side effect with a moveToPosition
    if (
      sideEffect.effect.type === EffectType.entangle &&
      (sideEffect as any).moveToPosition
    ) {
      // Triple entangle handling
      if ((sideEffect as any).tripleEntangle && move) {
        const modifiedMove = {
          ...move,
          position: (sideEffect as any).moveToPosition,
        };
        movePiece(modifiedMove);
        return;
      }

      // Engine directions now match visual expectations, no swap needed
      let searchDirection = clickedDirection;

      // If a specific direction was clicked that doesn't match the side effect's direction,
      // we need to find the corresponding move for that direction
      if (
        searchDirection &&
        searchDirection !== (sideEffect as any).direction
      ) {
        // Find the correct side effect for the clicked direction
        const correctSideEffect = potentialMoves
          .flatMap((m) => m.sideEffects)
          .find(
            (se) =>
              se.effect.type === EffectType.entangle &&
              (se as any).direction === searchDirection &&
              se.position.row === sideEffect.position.row &&
              se.position.col === sideEffect.position.col
          );

        if (correctSideEffect) {
          // Use the correct move for this direction
          const correctMove = findMoveFromSideEffect(correctSideEffect);
          if (correctMove) {
            // Create a copy of the move with the modified position
            const modifiedMove = {
              ...correctMove,
              position: (correctSideEffect as any).moveToPosition,
            };
            movePiece(modifiedMove);
            return;
          }
        }
      }

      // Original behavior (when direction matches or correct side effect not found)
      if (move) {
        const modifiedMove = {
          ...move,
          position: (sideEffect as any).moveToPosition,
        };
        movePiece(modifiedMove);
      }
    } else {
      // Regular side effect handling
      if (move) {
        movePiece(move);
      }
    }
  };

  // Selection locked state monitor for auto-execution
  useEffect(() => {
    // When selection becomes locked and we have a virus selected,
    // check if there's only one move available and auto-execute it
    if (
      selectionLocked &&
      selectedPiece &&
      selectedPiece.piece.type === PieceType.Virus
    ) {
      const validMoves = ZodiacEngineService.zodiacEngine.getValidMoves(
        selectedPiece.position,
        selectedPiece.piece
      );

      if (validMoves.length === 1) {
        setTimeout(() => {
          movePiece(validMoves[0]);
        }, 50);
      }
    }
  }, [selectionLocked]);

  const renderMoveIndicators = (): ReactNode[] => {
    const indicators: ReactNode[] = [];

    // DEBUG: Log moves for this square during bonus turn
    if (squareProps.moves.length > 0 && selectedPiece?.piece.type === PieceType.Penguin) {
    }

    squareProps.moves.forEach((move, index) => {
      // Skip rendering for rotate moves (penguin rotate) that target the center square
      if (
        move.moveType === MoveType.noMove &&
        move.position.row === squareProps.position.row &&
        move.position.col === squareProps.position.col
      ) {
        return; // Do not render a move indicator for the center square
      }

      // PENGUIN SPLIT HIGHLIGHTS: Detect if both capture and rotate moves exist for the same square
      const isCaptureMove = move.moveType === MoveType.capture;
      const isRotateMove = move.moveType === MoveType.rotate;
      const origin = selectedPiece?.position;
      let shouldSplitPenguin = false;

      if (origin && (isCaptureMove || isRotateMove)) {
        const otherType = isCaptureMove ? MoveType.rotate : MoveType.capture;
        shouldSplitPenguin = squareProps.moves.some(
          (m) =>
            m.moveType === otherType &&
            m.pieceId === move.pieceId &&
            m.position.row === move.position.row &&
            m.position.col === move.position.col
        );
      }

      if (shouldSplitPenguin && origin) {
        // ALWAYS render capture in top half, rotate in bottom half (consistent positioning)
        // Use observe mode classes when in observe mode
        let splitClass = "";
        if (isCaptureMove) {
          splitClass = isObserveMode ? styles.observeModeSplitCaptureTop : styles.splitCaptureTop;
        } else if (isRotateMove) {
          splitClass = isObserveMode ? styles.observeModeSplitRotateBottom : styles.splitRotateBottom;
        }

        indicators.push(
          <div
            key={`move-${index}-split-penguin-${move.moveType}`}
            className={splitClass}
            onClick={
              !isObserveMode && isMoveClickable(move)
                ? (e) => {
                    e.stopPropagation();
                    handleMoveClick(move);
                  }
                : undefined
            }
            title={`${move.moveType} move${isObserveMode ? ' (observing)' : ''}`}
          />
        );
        return; // Don't render the default full highlight for this move
      }

      // OCTOPUS SPLIT HIGHLIGHTS: Determine if both ink and regular move exist for the same piece and origin
      const isInkMove = move.moveType === MoveType.ink;
      const isRegularMove = move.moveType === MoveType.regular;
      // Only split if both exist for this piece and origin
      let shouldSplit = false;
      if (origin && (isInkMove || isRegularMove)) {
        const otherType = isInkMove ? MoveType.regular : MoveType.ink;
        shouldSplit = squareProps.moves.some(
          (m) =>
            m.moveType === otherType &&
            m.pieceId === move.pieceId &&
            origin.row === selectedPiece.position.row &&
            origin.col === selectedPiece.position.col
        );
      }
      if (shouldSplit && origin) {
        // Determine direction (up/down/left/right) for this move
        let direction: "up" | "down" | "left" | "right" | undefined;
        const dRow = move.position.row - origin.row;
        const dCol = move.position.col - origin.col;
        if (dRow === -1 && dCol === 0) direction = "down";
        else if (dRow === 1 && dCol === 0) direction = "up";
        else if (dRow === 0 && dCol === -1) direction = "left";
        else if (dRow === 0 && dCol === 1) direction = "right";

        // Pick split class based on move type and direction
        // Use observe mode classes when in observe mode
        let splitClass = "";
        if (isInkMove) {
          // For dark player (flipped board), swap up/down for ink highlights
          const isDarkPlayer = !isLightPlayer();
          if (isObserveMode) {
            if (direction === "up") splitClass = isDarkPlayer ? styles.observeModeSplitInkBottom : styles.observeModeSplitInkTop;
            else if (direction === "down") splitClass = isDarkPlayer ? styles.observeModeSplitInkTop : styles.observeModeSplitInkBottom;
            else if (direction === "left") splitClass = styles.observeModeSplitInkLeft;
            else if (direction === "right") splitClass = styles.observeModeSplitInkRight;
          } else {
            if (direction === "up") splitClass = isDarkPlayer ? styles.splitInkBottom : styles.splitInkTop;
            else if (direction === "down") splitClass = isDarkPlayer ? styles.splitInkTop : styles.splitInkBottom;
            else if (direction === "left") splitClass = styles.splitInkLeft;
            else if (direction === "right") splitClass = styles.splitInkRight;
          }
        } else if (isRegularMove) {
          if (isObserveMode) {
            // Use observe mode classes for regular moves (grey instead of green)
            if (direction === "up") splitClass = styles.observeModeSplitRegularTop;
            else if (direction === "down") splitClass = styles.observeModeSplitRegularBottom;
            else if (direction === "left") splitClass = styles.observeModeSplitRegularLeft;
            else if (direction === "right") splitClass = styles.observeModeSplitRegularRight;
          } else {
            if (direction === "up") splitClass = styles.splitRegularTop;
            else if (direction === "down") splitClass = styles.splitRegularBottom;
            else if (direction === "left") splitClass = styles.splitRegularLeft;
            else if (direction === "right") splitClass = styles.splitRegularRight;
          }
        }

        indicators.push(
          <div
            key={`move-${index}-split-${move.moveType}`}
            className={splitClass}
            onClick={
              !isObserveMode && isMoveClickable(move)
                ? (e) => {
                    e.stopPropagation();
                    handleMoveClick(move);
                  }
                : undefined
            }
            title={`${move.moveType} move (${direction})${isObserveMode ? ' (observing)' : ''}`}
          />
        );
        return; // Don't render the default full highlight for this move
      }

      let indicatorClass = "";

      // Use observe mode classes when in observe mode, otherwise use normal classes
      if (isObserveMode) {
        switch (move.moveType) {
          case MoveType.capture:
            indicatorClass = styles.observeModeCapture;
            break;
          case MoveType.consume:
            indicatorClass = styles.observeModeConsume;
            break;
          case MoveType.swing:
            indicatorClass = styles.observeModeSwing;
            break;
          case MoveType.regular:
          case MoveType.leap:
            indicatorClass = styles.observeModeRegular;
            break;
          case MoveType.trample:
            indicatorClass =
              squareProps.square.Pieces.length === 0
                ? styles.observeModeTrampleEmpty
                : styles.observeModeTrample;
            break;
          case MoveType.faint:
            indicatorClass = styles.observeModeFaint;
            break;
          case MoveType.lane:
            indicatorClass = styles.observeModeLane;
            break;
          case MoveType.house:
            indicatorClass = styles.observeModeHouse;
            break;
          case MoveType.infect:
            indicatorClass = styles.observeModeInfect;
            break;
          case MoveType.traverse:
            indicatorClass = styles.observeModeTraverse;
            break;
          case MoveType.endTurn:
            indicatorClass = styles.observeModeRegular;
            break;
          case MoveType.rotate:
            indicatorClass = styles.observeModeRotate;
            break;
          default:
            break;
        }
      } else {
        switch (move.moveType) {
          case MoveType.capture:
            indicatorClass = styles.potentialMoveCapture;
            break;
          case MoveType.consume:
            indicatorClass = styles.potentialMoveConsume;
            break;
          case MoveType.swing:
            indicatorClass = styles.potentialMoveSwing;
            break;
          case MoveType.regular:
          case MoveType.leap:
            indicatorClass = styles.potentialMoveRegular;
            break;
          case MoveType.trample:
            indicatorClass =
              squareProps.square.Pieces.length === 0
                ? styles.potentialMoveTrampleEmpty
                : `${styles.potentialMoveTrample} ${styles.potentialMoveCapture}`;
            break;
          case MoveType.faint:
            indicatorClass = styles.potentialMoveFaint;
            break;
          case MoveType.lane:
            indicatorClass = styles.potentialMoveLane;
            break;
          case MoveType.house:
            indicatorClass = styles.potentialMoveHouse;
            break;
          case MoveType.infect:
            indicatorClass = styles.potentialMoveInfect;
            break;
          case MoveType.traverse:
            indicatorClass = styles.potentialMoveTraverse;
            break;
          case MoveType.endTurn:
            indicatorClass = styles.potentialMoveEndTurn;
            break;
          case MoveType.rotate:
            // Penguin rotation - use directional indicator based on rotation direction
            const rotDir = (move.sideEffects.find(se => se.effect.type === EffectType.rotate) as any)?.rotateDirection;
            if (rotDir) {
              let dirStr = "";
              if (rotDir.dRow === 1 && rotDir.dCol === 0) dirStr = "Up";
              else if (rotDir.dRow === 1 && rotDir.dCol === 1) dirStr = "UpRight";
              else if (rotDir.dRow === 0 && rotDir.dCol === 1) dirStr = "Right";
              else if (rotDir.dRow === -1 && rotDir.dCol === 1) dirStr = "DownRight";
              else if (rotDir.dRow === -1 && rotDir.dCol === 0) dirStr = "Down";
              else if (rotDir.dRow === -1 && rotDir.dCol === -1) dirStr = "DownLeft";
              else if (rotDir.dRow === 0 && rotDir.dCol === -1) dirStr = "Left";
              else if (rotDir.dRow === 1 && rotDir.dCol === -1) dirStr = "UpLeft";

              indicatorClass = styles[`penguinRotate${dirStr}Indicator`] || styles.penguinRotateIndicator;
            } else {
              indicatorClass = styles.penguinRotateIndicator;
            }
            break;
          default:
            break;
        }
      }

      indicators.push(
        <div
          key={`move-${index}`}
          className={isObserveMode ? `${styles.observeModeIndicator} ${indicatorClass}` : `${styles.moveIndicator} ${indicatorClass}`}
          onClick={
            !isObserveMode && isMoveClickable(move)
              ? (e) => {
                  e.stopPropagation();
                  handleMoveClick(move);
                }
              : undefined
          }
          title={`${move.moveType} move${isObserveMode ? ' (observing)' : ''}`}
        />
      );

      // Skip bonus turn indicators in observe mode (they're informational for the opponent)
      if (isObserveMode) {
        return;
      }

      // Add bonus turn indicator for swing moves
      if (move.moveType === MoveType.swing) {
        indicators.push(
          <div
            key={`swing-bonus-${index}`}
            className={styles.swingBonusIndicator}
            onClick={
              isMoveClickable(move)
                ? (e) => {
                    e.stopPropagation();
                    handleMoveClick(move);
                  }
                : undefined
            }
            title="Swing move grants bonus turn"
          >
            +⬆
          </div>
        );
      }

      // Add bonus turn indicator for consume moves (monkey eating fungi)
      if (move.moveType === MoveType.consume) {
        indicators.push(
          <div
            key={`consume-bonus-${index}`}
            className={styles.consumeBonusIndicator}
            onClick={
              isMoveClickable(move)
                ? (e) => {
                    e.stopPropagation();
                    handleMoveClick(move);
                  }
                : undefined
            }
            title="Consume move grants bonus turn"
          >
            +⬆
          </div>
        );
      }

      // Add bonus turn indicator for virus moves (except no-move/end-turn)
      if (
        selectedPiece &&
        selectedPiece.piece.type === PieceType.Virus &&
        move.moveType !== MoveType.noMove &&
        move.moveType !== MoveType.endTurn
      ) {
        indicators.push(
          <div
            key={`virus-bonus-${index}`}
            className={styles.virusBonusIndicator}
            onClick={
              isMoveClickable(move)
                ? (e) => {
                    e.stopPropagation();
                    handleMoveClick(move);
                  }
                : undefined
            }
            title="Virus move grants bonus turn"
          >
            +⬆
          </div>
        );
      }
    });

    squareProps.sideEffects.forEach((sideEffect, index) => {
      // Use purple highlight for entangle side effects
      if (sideEffect.effect.type === EffectType.entangle) {
        // Check if this is a split or triple entangle (has direction property)
        const hasDirection = (sideEffect as any).direction !== undefined;
        const isTripleEntangle = (sideEffect as any).tripleEntangle === true;

        if (hasDirection) {
          const direction = (sideEffect as any).direction;
          const orthogonalDirection = (sideEffect as any).orthogonalDirection;

          // Determine the appropriate class based on direction and octopus position
          let tripleEntangleClass = "";

          if (isTripleEntangle) {
            // Map direction + orthogonalDirection to the appropriate CSS class
            // In observe mode, use greyed-out versions (same classes but with observe mode pointer-events: none behavior)
            if (direction === "left") {
              tripleEntangleClass =
                orthogonalDirection === "up"
                  ? styles.tripleEntangleLeftFromAbove
                  : styles.tripleEntangleLeftFromBelow;
            } else if (direction === "right") {
              tripleEntangleClass =
                orthogonalDirection === "up"
                  ? styles.tripleEntangleRightFromAbove
                  : styles.tripleEntangleRightFromBelow;
            } else if (direction === "up") {
              tripleEntangleClass =
                orthogonalDirection === "left"
                  ? styles.tripleEntangleUpFromLeft
                  : styles.tripleEntangleUpFromRight;
            } else if (direction === "down") {
              tripleEntangleClass =
                orthogonalDirection === "left"
                  ? styles.tripleEntangleDownFromLeft
                  : styles.tripleEntangleDownFromRight;
            }

            indicators.push(
              <div
                key={`sideEffect-${index}-${direction}`}
                className={tripleEntangleClass}
                style={isObserveMode ? { opacity: 0.25, pointerEvents: 'none', filter: 'grayscale(70%)' } : undefined}
                onClick={
                  !isObserveMode && isSideEffectClickable(sideEffect)
                    ? (e) => {
                        e.stopPropagation();
                        handleSideEffectClick(sideEffect, direction);
                      }
                    : undefined
                }
                onMouseEnter={!isObserveMode ? () => handleEntangleHover(sideEffect, true) : undefined}
                onMouseLeave={!isObserveMode ? () => handleEntangleHover(sideEffect, false) : undefined}
                title={`Entangle (move ${direction})${
                  isTripleEntangle ? " - triple" : ""
                }${isObserveMode ? " (observing)" : ""}`}
              />
            );
          } else {
            // For non-triple entangles, use the existing small entangle classes
            // In observe mode, use observe mode split entangle classes
            if (direction === "left") {
              indicators.push(
                <div
                  key={`sideEffect-${index}-left`}
                  className={isObserveMode ? styles.observeModeSplitEntangleLeft : styles.smallEntangleLeft}
                  onClick={
                    !isObserveMode && isSideEffectClickable(sideEffect)
                      ? (e) => {
                          e.stopPropagation();
                          handleSideEffectClick(sideEffect, "left");
                        }
                      : undefined
                  }
                  onMouseEnter={!isObserveMode ? () => handleEntangleHover(sideEffect, true) : undefined}
                  onMouseLeave={!isObserveMode ? () => handleEntangleHover(sideEffect, false) : undefined}
                  title={`Entangle (move left)${isObserveMode ? " (observing)" : ""}`}
                />
              );
            } else if (direction === "right") {
              indicators.push(
                <div
                  key={`sideEffect-${index}-right`}
                  className={isObserveMode ? styles.observeModeSplitEntangleRight : styles.smallEntangleRight}
                  onClick={
                    !isObserveMode && isSideEffectClickable(sideEffect)
                      ? (e) => {
                          e.stopPropagation();
                          handleSideEffectClick(sideEffect, "right");
                        }
                      : undefined
                  }
                  onMouseEnter={!isObserveMode ? () => handleEntangleHover(sideEffect, true) : undefined}
                  onMouseLeave={!isObserveMode ? () => handleEntangleHover(sideEffect, false) : undefined}
                  title={`Entangle (move right)${isObserveMode ? " (observing)" : ""}`}
                />
              );
            } else if (direction === "up") {
              indicators.push(
                <div
                  key={`sideEffect-${index}-top`}
                  className={isObserveMode ? styles.observeModeSplitEntangleTop : styles.smallEntangleTop}
                  onClick={
                    !isObserveMode && isSideEffectClickable(sideEffect)
                      ? (e) => {
                          e.stopPropagation();
                          handleSideEffectClick(sideEffect, "up");
                        }
                      : undefined
                  }
                  onMouseEnter={!isObserveMode ? () => handleEntangleHover(sideEffect, true) : undefined}
                  onMouseLeave={!isObserveMode ? () => handleEntangleHover(sideEffect, false) : undefined}
                  title={`Entangle (move up)${isObserveMode ? " (observing)" : ""}`}
                />
              );
            } else if (direction === "down") {
              indicators.push(
                <div
                  key={`sideEffect-${index}-bottom`}
                  className={isObserveMode ? styles.observeModeSplitEntangleBottom : styles.smallEntangleBottom}
                  onClick={
                    !isObserveMode && isSideEffectClickable(sideEffect)
                      ? (e) => {
                          e.stopPropagation();
                          handleSideEffectClick(sideEffect, "down");
                        }
                      : undefined
                  }
                  onMouseEnter={!isObserveMode ? () => handleEntangleHover(sideEffect, true) : undefined}
                  onMouseLeave={!isObserveMode ? () => handleEntangleHover(sideEffect, false) : undefined}
                  title={`Entangle (move down)${isObserveMode ? " (observing)" : ""}`}
                />
              );
            }
          }

          // Add the capture highlight if this square also has a piece that can be captured
          const shouldShowCaptureHighlight =
            squareProps.square.Pieces.length > 0 &&
            squareProps.square.Pieces.some((p) => {
              // The SideEffect interface should have a 'targets' property but it might be typed incorrectly
              // We'll use a type cast to access it safely
              const targetPieces = (sideEffect as any).targets || [];
              const isEnemyPiece =
                targetPieces.length > 0 &&
                p.isLight !== targetPieces[0].isLight;
              return isEnemyPiece;
            });

          if (shouldShowCaptureHighlight) {
            indicators.push(
              <div
                key={`capture-${index}`}
                className={styles.potentialOctopusCapture}
                style={isObserveMode ? { opacity: 0.25, pointerEvents: 'none', filter: 'grayscale(70%)' } : undefined}
                onClick={
                  !isObserveMode && isSideEffectClickable(sideEffect)
                    ? (e) => {
                        e.stopPropagation();
                        handleSideEffectClick(sideEffect, direction);
                      }
                    : undefined
                }
                title={`Capture or entangle${isObserveMode ? " (observing)" : ""}`}
              />
            );
          }
        } else {
          // Regular entangle - use full square highlight
          indicators.push(
            <div
              key={`sideEffect-${index}`}
              className={isObserveMode
                ? `${styles.observeModeIndicator} ${styles.observeModeEntangle}`
                : `${styles.moveIndicator} ${styles.potentialMoveEntangle}`}
              onClick={
                !isObserveMode && isSideEffectClickable(sideEffect)
                  ? (e) => {
                      e.stopPropagation();
                      handleSideEffectClick(sideEffect);
                    }
                  : undefined
              }
              onMouseEnter={!isObserveMode ? () => handleEntangleHover(sideEffect, true) : undefined}
              onMouseLeave={!isObserveMode ? () => handleEntangleHover(sideEffect, false) : undefined}
              title={`Entangle effect${isObserveMode ? " (observing)" : ""}`}
            />
          );
        }
      } else if (sideEffect.effect.type === EffectType.rotate) {
        // Skip - rotation indicators are rendered from moves, not side effects
        return;
      } else {
        // Handle other side effect types normally
        indicators.push(
          <div
            key={`sideEffect-${index}`}
            className={styles.sideEffectIndicator}
            style={isObserveMode ? { opacity: 0.25, pointerEvents: 'none', filter: 'grayscale(70%)' } : undefined}
            onClick={
              !isObserveMode && isSideEffectClickable(sideEffect)
                ? (e) => {
                    e.stopPropagation();
                    handleSideEffectClick(sideEffect);
                  }
                : undefined
            }
            title={`Side effect: ${sideEffect.effect.type}${isObserveMode ? " (observing)" : ""}`}
          >
            {sideEffect.effect.type.toString()}
          </div>
        );
      }
    });

    // Add destination highlight indicator if this square is the target for hover
    if (isDestinationHighlighted) {
      indicators.push(
        <div
          key="movement-destination-highlight"
          className={styles.moveDestinationHighlight}
        />
      );
    }

    // Add tutorial goal row highlight
    if (shouldShowGoalRowHighlight()) {
      indicators.push(
        <div key="tutorial-goal-row" className={styles.tutorialGoalRow} />
      );
    }

    // Add tutorial piece glow for first click guidance
    if (shouldShowTutorialPieceGlow()) {
      indicators.push(
        <div key="tutorial-piece-glow" className={styles.tutorialPieceGlow} />
      );
    }

    // Add tutorial danger row highlight (red squares for blocking)
    if (shouldShowTutorialDangerRow()) {
      indicators.push(
        <div key="tutorial-danger-row" className={styles.tutorialDangerRow} />
      );
    }

    // Add tutorial enemy target highlight (golden glow around enemies to capture)
    if (shouldShowTutorialEnemyTarget()) {
      indicators.push(
        <div
          key="tutorial-enemy-target"
          className={styles.tutorialEnemyTarget}
        />
      );
    }

    // Add full-square virus end turn highlight during bonus turn
    // Uses engine state (activeBonusTurnPieceId) so it works during the 300ms window too
    const virusBonusPieceOnSquare = squareProps.square.Pieces.find(
      p => p.id === gameState.activeBonusTurnPieceId && p.type === PieceType.Virus
    );
    if (virusBonusPieceOnSquare) {
      let hasEndTurnMove = squareProps.moves.some(
        (move) => move.moveType === MoveType.endTurn
      );
      if (!hasEndTurnMove && gameState.activeBonusTurnPieceId) {
        const validMoves = ZodiacEngineService.zodiacEngine.getValidMoves(
          squareProps.position, virusBonusPieceOnSquare
        );
        hasEndTurnMove = validMoves.some(m => m.moveType === MoveType.endTurn);
      }

      if (hasEndTurnMove) {
        indicators.push(
          <div
            key="virus-end-turn-highlight"
            className={styles.virusEndTurnHighlight}
            title="Click to end virus bonus turn"
          >
            <span className={styles.virusEndTurnTooltip}>End Turn</span>
          </div>
        );
      }
    }

    // Add placement indicator for empty home row squares during piece placement
    if (squareProps.isHomeRowHighlight && squareProps.square.Pieces.length === 0) {
      indicators.push(
        <div
          key="setup-indicator"
          className={styles.setupArrowIndicator}
        />
      );
    }

    // Add loss effect indicators for tutorial feedback
    const activeLossEffect = squareProps.tutorialInfo?.activeLossEffect;
    if (
      activeLossEffect &&
      activeLossEffect.position.row === squareProps.position.row &&
      activeLossEffect.position.col === squareProps.position.col
    ) {
      let lossEffectClassName = "";
      switch (activeLossEffect.type) {
        case "capture":
          lossEffectClassName = styles.lossSlashEffect;
          break;
        case "race":
          lossEffectClassName = styles.lossPulseEffect;
          break;
        case "general":
        default:
          lossEffectClassName = styles.lossShutterEffect;
          break;
      }

      indicators.push(
        <div key="loss-effect" className={lossEffectClassName} />
      );
    }

    return indicators;
  };

  // Function to render terrain overlay (in front of pieces) for specific tutorials
  const renderTerrainOverlay = (): ReactNode => {
    if (!squareProps.tutorialInfo) return null;

    const { constellation, tutorialIndex } = squareProps.tutorialInfo;

    // Get terrain data for this tutorial
    const terrainData = getTutorialTerrain(constellation, tutorialIndex);
    const terrainType =
      terrainData[squareProps.position.row]?.[squareProps.position.col];

    // Check if this terrain should render as an overlay
    if (
      terrainType !== undefined &&
      shouldTerrainRenderAsOverlay(constellation, tutorialIndex, terrainType)
    ) {
      const terrainImagePath = getTerrainImagePath(terrainType);

      return (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url('${terrainImagePath}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            pointerEvents: "none", // Allow clicks to pass through to pieces
            zIndex: 10, // Render above pieces
          }}
        />
      );
    }

    return null;
  };

  return (
    <div
      className={getStyle()}
      style={squareProps.designateMode ? { cursor: 'crosshair' } : undefined}
      onClick={(e) => {
        e.stopPropagation();

        // Disable player interaction in Rules mode
        if (gameState.mode === Mode.Rules) {
          return;
        }

        // Handle clicks anywhere on the square (including on pieces and indicators)
        // Special case: Virus square during bonus turn — end turn on click
        // Uses engine state (activeBonusTurnPieceId) so it works during the 300ms window too
        const virusBonusPiece = squareProps.square.Pieces.find(
          p => p.id === gameState.activeBonusTurnPieceId && p.type === PieceType.Virus
        );
        if (virusBonusPiece) {
          let endTurnMove = squareProps.moves.find(m => m.moveType === MoveType.endTurn);
          if (!endTurnMove) {
            // During 300ms window, moves may not be in props yet — fetch from engine
            const validMoves = ZodiacEngineService.zodiacEngine.getValidMoves(squareProps.position, virusBonusPiece);
            endTurnMove = validMoves.find(m => m.moveType === MoveType.endTurn);
          }
          if (endTurnMove) {
            if (!selectedPiece || selectedPiece.piece.id !== virusBonusPiece.id) {
              updateSelectedPiece({ piece: virusBonusPiece, position: squareProps.position });
              setSelectionLocked(true);
            }
            movePiece(endTurnMove);
            return;
          }
        }

        // Handle mobile click-to-place FIRST if handler is provided (for piece placement mode)
        // Don't check e.target === e.currentTarget for mobile placement - allow clicks anywhere on the square
        if (squareProps.onSquareClick) {
          squareProps.onSquareClick(squareProps.position);
          return;
        }

        // Only handle clicks directly on the div (not on move indicators or pieces) for normal behavior
        if (e.target === e.currentTarget) {

          // If this square is empty
          if (squareProps.square.Pieces.length === 0) {
            // Check if this is a valid move destination for the selected piece
            const isValidMoveDestination = squareProps.moves.length > 0;

            if (isValidMoveDestination && selectedPiece && !isObserveMode) {
              // Find all moves for this position
              const movesForThisSquare = squareProps.moves.filter(
                (m) =>
                  m.position.row === squareProps.position.row &&
                  m.position.col === squareProps.position.col
              );
              // Pick the first move (existing behavior)
              const move = movesForThisSquare[0];
              if (move) {
                handleMoveClick(move);
                return;
              }
            }

            // Handle as an empty square click (deselection) if not locked and no bonus turn
            if (!selectionLocked && !gameState.activeBonusTurnPieceId) {
              deselectPiece();
            }
          }
        }
      }}
      onDrop={(event) => {
        // Disable player interaction in Rules mode
        if (gameState.mode === Mode.Rules) {
          event.preventDefault();
          return;
        }
        squareProps.onDrop(event, squareProps.position);
      }}
      onDragOver={(event) => {
        // Disable player interaction in Rules mode
        if (gameState.mode === Mode.Rules) {
          event.preventDefault();
          return;
        }
        squareProps.onDragOver(event);
      }}
      onContextMenu={(e) => {
        // King Guess taunt feature - right-click to mark any piece with crown
        e.preventDefault();
        e.stopPropagation();

        const clickedPiece = squareProps.square.Pieces[0];
        if (!clickedPiece) return;

        // Toggle: if same piece already guessed, clear it
        const existingGuess = getKingGuessForPiece(clickedPiece.id);
        if (existingGuess) {
          clearKingGuess();
        } else {
          setKingGuess(squareProps.position, clickedPiece.id);
        }
      }}
    >
      {renderMoveIndicators()}

      <SquareRenderer
        pieces={squareProps.square.Pieces}
        handleSelect={handlePieceClick}
        position={squareProps.position}
        onPieceDragStart={squareProps.onPieceDragStart}
        designateMode={squareProps.designateMode}
      />

      {renderTerrainOverlay()}
    </div>
  );
}

export default Square;
